import { format, parseISO } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '@predoc/cow';
import {
  aggregateLabToc,
  buildLabPanelSummary,
  classifyLabPanels,
  countPanelsByCategory,
  displayLabTableCell,
  isOutsideReferenceRange,
  panelShouldDisplay,
  partitionLabPanelRows,
  type ClassifiedLabsTemplatePanel,
  type LabsTemplatePanel,
  type LabsTemplateRow,
} from './labsClassification';
import { DocumentTemplateShell, TemplateEmptyState } from './TemplateShared';
import {
  TemplateSummaryCard,
  type TemplateSummaryItem,
} from './TemplateSummaryCard';
import {
  DASH,
  extractMarkupText,
  formatPractitionerAddress,
  formatPractitionerNameWithRole,
  formatPractitionerPhone,
  formatRoleSpecialty,
  formatShortDate,
} from './templateUtils';
import type { TemplatePatient, TemplatePractitioner } from './templateUtils';

export type LabsTemplatePractitioner = TemplatePractitioner;

export type { LabsTemplatePanel, LabsTemplateRow };

export type LabsTemplatePatient = TemplatePatient;

export interface LabsAndDiagnosticsTemplateProps {
  panels: LabsTemplatePanel[];
  patient?: LabsTemplatePatient;
  filename?: string;
  generatedAt?: Date;
  pageInfo?: { current: number; total: number };
  highlightPanelId?: string;
  reportDate?: string;
}

const getPanelStudyDates = (panel: ClassifiedLabsTemplatePanel): string[] => {
  const dates = [
    panel.dateOfStudy,
    ...(panel.rows ?? []).map((row) => row.dateOfStudy),
  ]
    .filter((value): value is string => !!value?.trim())
    .sort((a, b) => parseISO(a).getTime() - parseISO(b).getTime());

  return dates;
};

const formatImagingHeaderDateTime = (
  iso?: string | null,
): string | undefined => {
  if (!iso?.trim()) return undefined;
  const parsed = parseISO(iso.trim());
  if (Number.isNaN(parsed.getTime())) return iso.trim();
  return format(parsed, 'MMM d, yyyy, h:mm a');
};

const buildTocSummaryItems = (
  panels: ClassifiedLabsTemplatePanel[],
  category: 'lab' | 'imaging',
): TemplateSummaryItem[] =>
  aggregateLabToc(panels, category).map((item) => ({
    id: item.id,
    label: `${item.label} (${item.count})`,
    anchorId: item.anchorId,
  }));

const tableHeaders = [
  'Name',
  'Value',
  'Unit',
  'Interpretation',
  'Reference range',
  'Notes',
  'Also found on',
] as const;

interface PractitionerColumnProps {
  practitioner?: LabsTemplatePractitioner;
}

const PractitionerColumn = ({ practitioner }: PractitionerColumnProps) => (
  <div className="flex flex-1 flex-col gap-1">
    <div className="flex flex-col gap-1 text-[10px] leading-tight text-black">
      <p>
        <span className="text-gray-800">Name: </span>
        {formatPractitionerNameWithRole(practitioner)}
      </p>
      <p>
        <span className="text-gray-800">Speciality: </span>
        {formatRoleSpecialty(practitioner)}
      </p>
      <p>
        <span className="text-gray-800">Phone: </span>
        {formatPractitionerPhone(practitioner, DASH)}
      </p>
      <p>
        <span className="text-gray-800">Address: </span>
        {formatPractitionerAddress(practitioner)}
      </p>
    </div>
  </div>
);

interface LabPanelSectionProps {
  panel: ClassifiedLabsTemplatePanel;
  index: number;
  highlightPanelId?: string;
}

interface ImagingPanelSectionProps {
  panel: ClassifiedLabsTemplatePanel;
  index: number;
  highlightPanelId?: string;
}

const LabPanelSection = ({
  panel,
  index,
  highlightPanelId,
}: LabPanelSectionProps) => {
  const { rowsWithData } = partitionLabPanelRows(panel.rows ?? []);
  const summary = buildLabPanelSummary(rowsWithData);
  const performer = panel.performers?.[0];
  const interpreter = panel.interpreters?.[0];
  const panelTitle = panel.labPanel?.trim() || `Panel ${index + 1}`;
  const rowDates = rowsWithData
    .map((row) => row.dateOfStudy)
    .filter((value): value is string => !!value)
    .sort();
  const panelDate = rowDates[0] ?? panel.dateOfStudy;
  const isHighlighted = !!highlightPanelId && panel.id === highlightPanelId;
  const panelBorder = isHighlighted ? 'border-amber-300' : 'border-gray-200';

  return (
    <Card
      id={`lab-panel-${panel.id}`}
      tabIndex={isHighlighted ? -1 : undefined}
      className={`flex flex-col overflow-hidden rounded-sm border-2 bg-white p-0 pb-4 shadow-none ${panelBorder}`}
    >
      <CardHeader className="flex flex-col gap-2 rounded-none bg-slate-700 px-5 pt-5 pb-3 text-white">
        <CardTitle className="text-[14px] leading-tight font-semibold text-white">
          {panelTitle}
        </CardTitle>
        <div className="text-[10px] leading-tight">
          <span className="font-semibold">Dates: </span>
          {formatShortDate(panelDate)}
        </div>
      </CardHeader>

      <CardContent className="mx-5 mt-3 flex flex-col gap-3 p-0">
        <div className="flex flex-col gap-2 overflow-hidden rounded-sm border border-gray-400">
          <div className="flex items-center gap-2 bg-slate-200 px-3 pt-2.5 pb-1.5 text-[10px] leading-tight font-medium text-gray-900">
            <p className="flex-1">Performer</p>
            <p className="flex-1">Interpreter</p>
          </div>
          <div className="flex gap-2 px-3 pb-2">
            <PractitionerColumn practitioner={performer} />
            <PractitionerColumn practitioner={interpreter} />
          </div>
        </div>

        <div className="flex flex-col gap-2 overflow-hidden rounded-sm border border-gray-400">
          <div className="bg-slate-200 px-3 pt-2.5 pb-1.5">
            <p className="text-[10px] leading-tight font-medium text-gray-900">
              Summary
            </p>
          </div>
          <div className="flex flex-col gap-1 px-3 pb-2 text-[10px] leading-tight text-black">
            <p>
              <span className="text-gray-800">Number of results: </span>
              <span className="font-semibold">{summary.totalLabs}</span>
            </p>
            <p>
              <span className="text-gray-800">
                Labs outside of reference range:{' '}
              </span>
              <span className="font-semibold">{summary.outsideCount}</span>
            </p>
            {summary.outsideNames.length > 0 ? (
              <ul className="mt-1 list-disc pl-4">
                {summary.outsideNames.map((labName, rowIdx) => (
                  <li key={`${panel.id}-oor-${rowIdx}`}>{labName}</li>
                ))}
              </ul>
            ) : null}
          </div>
        </div>

        <div className="overflow-hidden rounded-sm border border-zinc-200">
          <div className="flex bg-slate-100">
            {tableHeaders.map((header, idx) => (
              <div
                key={header}
                className={`flex flex-1 border-b border-zinc-300 px-2 py-3 ${
                  idx < tableHeaders.length - 1 ? 'border-r' : ''
                }`}
              >
                <p className="flex-1 text-[9px] leading-tight font-black tracking-[0.35px] text-zinc-500 uppercase">
                  {header}
                </p>
              </div>
            ))}
          </div>

          {rowsWithData.map((row, rowIdx) => {
            const zebra = rowIdx % 2 === 1 ? 'bg-gray-50' : 'bg-white';
            const rowBg = isOutsideReferenceRange(row) ? 'bg-amber-100' : zebra;
            const cells = [
              row.labTest,
              row.value,
              row.units,
              row.interpretation,
              undefined,
              undefined,
              undefined,
            ];
            const rowKey = `${panel.id}-${rowIdx}`;

            return (
              <div
                key={rowKey}
                className={`flex border-t border-zinc-200 ${rowBg}`}
              >
                {cells.map((cell, colIdx) => (
                  <div
                    key={tableHeaders[colIdx]}
                    className={`flex flex-1 px-2 py-2 ${
                      colIdx < cells.length - 1
                        ? 'border-r border-zinc-200'
                        : ''
                    }`}
                  >
                    <p className="flex-1 text-[9px] leading-tight text-zinc-950">
                      {displayLabTableCell(cell)}
                    </p>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

const ImagingPanelSection = ({
  panel,
  index,
  highlightPanelId,
}: ImagingPanelSectionProps) => {
  const rows = panel.rows ?? [];
  const performer = panel.performers?.[0];
  const interpreter = panel.interpreters?.[0];
  const panelTitle = panel.labPanel?.trim() || `Imaging study ${index + 1}`;
  const studyDates = getPanelStudyDates(panel);
  const startDate = formatImagingHeaderDateTime(studyDates[0]);
  const endDate =
    studyDates.length > 1
      ? formatImagingHeaderDateTime(studyDates[studyDates.length - 1])
      : undefined;
  const isHighlighted = !!highlightPanelId && panel.id === highlightPanelId;
  const panelBorder = isHighlighted ? 'border-amber-300' : 'border-gray-200';
  const narrativeRows = rows
    .map((row) => {
      const label = row.labTest?.trim();
      const value = extractMarkupText(row.value)?.trim();
      if (label && value) return `${label}\n${value}`;
      return value || label || '';
    })
    .filter(Boolean);

  return (
    <Card
      id={`lab-panel-${panel.id}`}
      tabIndex={isHighlighted ? -1 : undefined}
      className={`flex flex-col overflow-hidden rounded-sm border-2 bg-white p-0 pb-4 shadow-none ${panelBorder}`}
    >
      <CardHeader className="flex flex-row items-center gap-0.5 rounded-none bg-slate-700 px-3.5 pt-4 pb-2.5 text-white">
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <CardTitle className="text-[11px] leading-[1.2] font-semibold text-white">
            {panelTitle}
          </CardTitle>
          {startDate || endDate ? (
            <div className="flex flex-wrap gap-3.5 text-[8px] leading-[1.2]">
              {startDate ? (
                <p>
                  <span className="font-semibold">Start:</span> {startDate}
                </p>
              ) : null}
              {endDate ? (
                <p>
                  <span className="font-semibold">End:</span> {endDate}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
        <span className="shrink-0 rounded-full bg-pink-100 px-2.5 py-px text-[8px] leading-4 font-medium tracking-[0.32px] text-zinc-600 uppercase">
          Imaging
        </span>
      </CardHeader>

      <CardContent className="mx-5 mt-3 flex flex-col gap-3 p-0">
        <div className="flex flex-col gap-2 overflow-hidden rounded-sm border border-gray-400">
          <div className="flex items-center gap-2 bg-slate-200 px-3 pt-2.5 pb-1.5 text-[10px] leading-tight font-medium text-gray-900">
            <p className="flex-1">Performer</p>
            <p className="flex-1">Interpreter</p>
          </div>
          <div className="flex gap-2 px-3 pb-2">
            <PractitionerColumn practitioner={performer} />
            <PractitionerColumn practitioner={interpreter} />
          </div>
        </div>

        {narrativeRows.length > 0 ? (
          <div className="flex flex-col gap-2 overflow-hidden rounded-sm border border-gray-400">
            <div className="bg-slate-200 px-3 pt-2.5 pb-1.5">
              <p className="text-[10px] leading-tight font-medium text-gray-900">
                Imaging study details
              </p>
            </div>
            <div className="flex flex-col gap-3 px-3 py-2 text-[10px] leading-tight whitespace-pre-line text-black">
              {narrativeRows.map((text, rowIdx) => (
                <p key={`${panel.id}-imaging-${rowIdx}`}>{text}</p>
              ))}
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};

export const LabsAndDiagnosticsTemplate = ({
  panels,
  patient,
  filename,
  generatedAt,
  pageInfo,
  highlightPanelId,
  reportDate,
}: LabsAndDiagnosticsTemplateProps) => {
  const classifiedPanels = classifyLabPanels(
    (panels ?? []).filter((panel) => (panel.rows?.length ?? 0) > 0),
  ).filter(panelShouldDisplay);
  const hasPanels = classifiedPanels.length > 0;
  const labsSummaryItems = buildTocSummaryItems(classifiedPanels, 'lab');
  const imagingSummaryItems = buildTocSummaryItems(classifiedPanels, 'imaging');

  return (
    <DocumentTemplateShell
      title="Labs & Diagnostics Report"
      patient={patient}
      filename={filename}
      generatedAt={generatedAt}
      pageInfo={pageInfo}
      headerBg="slate"
      reportDate={reportDate}
    >
      <section className="flex flex-1 flex-col gap-5 px-8 py-5">
        <div className="flex gap-5">
          <TemplateSummaryCard
            title="Labs & diagnostics"
            count={countPanelsByCategory(classifiedPanels, 'lab')}
            items={labsSummaryItems}
            tone="sky"
          />
          <TemplateSummaryCard
            title="Imaging"
            count={countPanelsByCategory(classifiedPanels, 'imaging')}
            items={imagingSummaryItems}
            tone="pink"
          />
        </div>
        {!hasPanels ? (
          <TemplateEmptyState message="No lab results available" />
        ) : (
          classifiedPanels.map((panel, index) =>
            panel.category === 'imaging' ? (
              <ImagingPanelSection
                key={panel.id ?? `panel-${index}`}
                panel={panel}
                index={index}
                highlightPanelId={highlightPanelId}
              />
            ) : (
              <LabPanelSection
                key={panel.id ?? `panel-${index}`}
                panel={panel}
                index={index}
                highlightPanelId={highlightPanelId}
              />
            ),
          )
        )}
      </section>
    </DocumentTemplateShell>
  );
};
